import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { MessageSquarePlus, Users, Building2, LogIn, LogOut as LogOutIcon, Truck, UserCheck, User, Search, VolumeX } from 'lucide-react';
import { Button } from '@/components/ui/button';
import NewChatDialog from './NewChatDialog';
import CreateGroupDialog from './CreateGroupDialog';
import { isRoomMuted } from '@/hooks/useUnreadCounts';

interface ChatRoom {
  id: string;
  type: string;
  name: string | null;
  created_at: string;
  lastMessage?: string;
  lastMessageTime?: string;
  otherUserName?: string;
  isMember?: boolean;
  icon_url?: string | null;
}

interface ContactWithChat {
  profileId: string;
  profileName: string;
  role: string;
  room?: ChatRoom;
}

interface ChatListProps {
  activeTab: string;
  onSelectRoom: (room: ChatRoom) => void;
  unreadByRoom?: Record<string, number>;
}

const ChatList = ({ activeTab, onSelectRoom, unreadByRoom = {} }: ChatListProps) => {
  const { user, role } = useAuth();
  const { t, lang } = useLanguage();
  const [rooms, setRooms] = useState<ChatRoom[]>([]);
  const [contacts, setContacts] = useState<ContactWithChat[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNewChat, setShowNewChat] = useState(false);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const typeMap: Record<string, string> = {
    chats: 'direct',
    groups: 'group',
    departments: 'department',
  };

  const fetchRooms = async () => {
    if (!user) return;
    setLoading(true);

    const roomType = typeMap[activeTab];

    if (roomType === 'direct') {
      // Fetch all profiles and roles
      const [{ data: profilesData }, { data: rolesData }, { data: myMemberships }] = await Promise.all([
        supabase.from('profiles_public' as any).select('id, name') as any,
        supabase.from('user_roles').select('user_id, role'),
        supabase.from('chat_room_members').select('room_id').eq('user_id', user.id),
      ]);

      const roleMap: Record<string, string> = {};
      (rolesData || []).forEach(r => { roleMap[r.user_id] = r.role; });

      const otherProfiles = (profilesData || []).filter(p => p.id !== user.id);
      const roomIds = (myMemberships || []).map(m => m.room_id);

      // Get direct and 2-member group rooms
      let directRooms: ChatRoom[] = [];
      if (roomIds.length > 0) {
        const { data: roomsData } = await supabase
          .from('chat_rooms')
          .select('*')
          .in('type', ['direct', 'group'])
          .in('id', roomIds);
        
        // Filter group rooms to only those with exactly 2 members
        const filteredRooms: ChatRoom[] = [];
        for (const room of roomsData || []) {
          if (room.type === 'direct') {
            filteredRooms.push(room);
          } else {
            const { count } = await supabase
              .from('chat_room_members')
              .select('id', { count: 'exact', head: true })
              .eq('room_id', room.id);
            if (count === 2) {
              filteredRooms.push(room);
            }
          }
        }
        directRooms = filteredRooms;
      }

      // For each direct room, find the other user and last message
      const roomToOtherUser: Record<string, string> = {};
      const otherUserToRoom: Record<string, ChatRoom> = {};

      for (const room of directRooms) {
        const { data: members } = await supabase
          .from('chat_room_members')
          .select('user_id')
          .eq('room_id', room.id)
          .neq('user_id', user.id);

        if (members && members.length > 0) {
          const otherId = members[0].user_id;
          roomToOtherUser[room.id] = otherId;

          // Get last message
          const { data: msgs } = await supabase
            .from('chat_messages')
            .select('content, created_at')
            .eq('room_id', room.id)
            .order('created_at', { ascending: false })
            .limit(1);

          const enrichedRoom: ChatRoom = { ...room, isMember: true };
          if (msgs && msgs.length > 0) {
            enrichedRoom.lastMessage = msgs[0].content;
            enrichedRoom.lastMessageTime = msgs[0].created_at;
          }
          otherUserToRoom[otherId] = enrichedRoom;
        }
      }

      // Build contacts list
      const contactsList: ContactWithChat[] = otherProfiles.map(p => ({
        profileId: p.id,
        profileName: p.name,
        role: roleMap[p.id] || 'employee',
        room: otherUserToRoom[p.id],
      }));

      // Sort: contacts with recent messages first, then others alphabetically
      contactsList.sort((a, b) => {
        if (a.room?.lastMessageTime && b.room?.lastMessageTime) {
          return new Date(b.room.lastMessageTime).getTime() - new Date(a.room.lastMessageTime).getTime();
        }
        if (a.room?.lastMessageTime) return -1;
        if (b.room?.lastMessageTime) return 1;
        if (a.room && !b.room) return -1;
        if (!a.room && b.room) return 1;
        return a.profileName.localeCompare(b.profileName);
      });

      setContacts(contactsList);
      setRooms([]);
    } else if (roomType === 'department') {
      const { data: myMemberships } = await supabase
        .from('chat_room_members')
        .select('room_id')
        .eq('user_id', user.id);

      const memberRoomIds = new Set(myMemberships?.map(m => m.room_id) || []);

      const { data: memberRooms } = await supabase
        .from('chat_rooms')
        .select('*')
        .eq('type', 'department');

      const deptRooms: ChatRoom[] = (memberRooms || []).map(r => ({
        ...r,
        isMember: memberRoomIds.has(r.id),
      }));

      for (const room of deptRooms) {
        if (room.isMember) {
          const { data: msgs } = await supabase
            .from('chat_messages')
            .select('content, created_at')
            .eq('room_id', room.id)
            .order('created_at', { ascending: false })
            .limit(1);
          if (msgs && msgs.length > 0) {
            room.lastMessage = msgs[0].content;
            room.lastMessageTime = msgs[0].created_at;
          }
        }
      }

      deptRooms.sort((a, b) => {
        if (!a.lastMessageTime) return 1;
        if (!b.lastMessageTime) return -1;
        return new Date(b.lastMessageTime).getTime() - new Date(a.lastMessageTime).getTime();
      });

      // Resolve signed URLs for room icons
      await resolveIconUrls(deptRooms);
      setRooms(deptRooms);
      setContacts([]);
    } else {
      // Groups
      const { data: memberRooms } = await supabase
        .from('chat_room_members')
        .select('room_id')
        .eq('user_id', user.id);

      if (!memberRooms || memberRooms.length === 0) {
        setRooms([]);
        setContacts([]);
        setLoading(false);
        return;
      }

      const roomIds = memberRooms.map(m => m.room_id);

      const { data: roomsData } = await supabase
        .from('chat_rooms')
        .select('*')
        .eq('type', roomType)
        .in('id', roomIds);

      const enrichedRooms: ChatRoom[] = [];

      for (const room of roomsData || []) {
        // Skip 2-member groups (those appear in chats tab)
        const { count } = await supabase
          .from('chat_room_members')
          .select('id', { count: 'exact', head: true })
          .eq('room_id', room.id);
        if (count !== null && count <= 2) continue;

        const enriched: ChatRoom = { ...room, isMember: true };
        const { data: msgs } = await supabase
          .from('chat_messages')
          .select('content, created_at')
          .eq('room_id', room.id)
          .order('created_at', { ascending: false })
          .limit(1);
        if (msgs && msgs.length > 0) {
          enriched.lastMessage = msgs[0].content;
          enriched.lastMessageTime = msgs[0].created_at;
        }
        enrichedRooms.push(enriched);
      }

      enrichedRooms.sort((a, b) => {
        if (!a.lastMessageTime) return 1;
        if (!b.lastMessageTime) return -1;
        return new Date(b.lastMessageTime).getTime() - new Date(a.lastMessageTime).getTime();
      });

      // Resolve signed URLs for room icons
      await resolveIconUrls(enrichedRooms);
      setRooms(enrichedRooms);
      setContacts([]);
    }

    setLoading(false);
  };

  // Helper to resolve storage paths to signed URLs for room icons
  const resolveIconUrls = async (rooms: ChatRoom[]) => {
    for (const room of rooms) {
      if (room.icon_url && !room.icon_url.startsWith('http')) {
        const { data } = await supabase.storage.from('chat-media').createSignedUrl(room.icon_url, 3600);
        if (data?.signedUrl) room.icon_url = data.signedUrl;
      }
    }
  };

  useEffect(() => {
    fetchRooms();
  }, [activeTab, user]);

  const handleJoinDepartment = async (roomId: string) => {
    if (!user) return;
    await supabase.from('chat_room_members').insert({ room_id: roomId, user_id: user.id });
    fetchRooms();
  };

  const handleLeaveDepartment = async (roomId: string) => {
    if (!user) return;
    await supabase.from('chat_room_members').delete().eq('room_id', roomId).eq('user_id', user.id);
    fetchRooms();
  };

  const handleContactClick = async (contact: ContactWithChat) => {
    if (contact.room) {
      onSelectRoom({ ...contact.room, otherUserName: contact.profileName });
      return;
    }
    // Create new group room for direct chat
    if (!user) return;
    const { data: newRoom } = await supabase
      .from('chat_rooms')
      .insert({ type: 'group', name: contact.profileName, created_by: user.id })
      .select()
      .single();

    if (newRoom) {
      await supabase.from('chat_room_members').insert([
        { room_id: newRoom.id, user_id: user.id },
        { room_id: newRoom.id, user_id: contact.profileId },
      ]);
      onSelectRoom({ ...newRoom, otherUserName: contact.profileName });
    }
  };

  const formatTime = (dateStr?: string) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleTimeString(lang === 'ar' ? 'en-US' : 'en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
  };

  const getDisplayName = (room: ChatRoom) => {
    if (room.type === 'direct') return room.otherUserName || 'مستخدم';
    return room.name || '';
  };

  const getAvatarColor = (name: string) => {
    const colors = ['#25D366', '#128C7E', '#075E54', '#34B7F1', '#00A884'];
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return colors[Math.abs(hash) % colors.length];
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'driver': return <Truck className="h-3 w-3" />;
      case 'customer': return <UserCheck className="h-3 w-3" />;
      default: return <User className="h-3 w-3" />;
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'driver': return t('driver');
      case 'customer': return t('customer');
      case 'admin': return 'مدير';
      default: return 'موظف';
    }
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#25D366] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden relative">
      <div className="px-3 py-2 bg-[#f6f6f6]">
        <div className="flex items-center gap-2 bg-white rounded-lg px-3 py-1.5">
          <Search className="h-4 w-4 text-[#54656f]" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="بحث..."
            className="flex-1 bg-transparent text-sm text-[#111b21] placeholder:text-[#667781] outline-none"
          />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        {/* Direct chats - show all contacts */}
        {activeTab === 'chats' && contacts.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-gray-500 p-4">
            <MessageSquarePlus className="h-12 w-12 mb-2 opacity-40" />
            <p className="text-sm">{t('noUsers')}</p>
          </div>
        )}

        {activeTab === 'chats' && contacts.map((contact) => {
          const avatarColor = getAvatarColor(contact.profileName);
          return (
            <div
              key={contact.profileId}
              className="flex items-center gap-3 px-4 py-3 hover:bg-[#f0f0f0] cursor-pointer border-b border-[#e9e9e9]"
              onClick={() => handleContactClick(contact)}
            >
              <Avatar className="h-12 w-12 shrink-0">
                <AvatarFallback style={{ backgroundColor: avatarColor }} className="text-white text-lg font-medium">
                  {contact.profileName.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-[#111b21] text-base truncate">{contact.profileName}</h4>
                  {contact.room?.lastMessageTime && <span className="text-[11px] text-[#667781]">{formatTime(contact.room.lastMessageTime)}</span>}
                </div>
                {contact.room?.lastMessage ? (
                  <p className="text-sm text-[#667781] truncate">{contact.room.lastMessage}</p>
                ) : (
                  <span className="text-[11px] text-[#667781] flex items-center gap-1">
                    {getRoleIcon(contact.role)}
                    {getRoleLabel(contact.role)}
                  </span>
                )}
              </div>
            </div>
          );
        })}

        {/* Groups and Departments */}
        {activeTab !== 'chats' && rooms.length === 0 && activeTab !== 'departments' && (
          <div className="flex flex-col items-center justify-center h-full text-gray-500 p-4">
            <MessageSquarePlus className="h-12 w-12 mb-2 opacity-40" />
            <p className="text-sm">{t('noMessages')}</p>
          </div>
        )}

        {activeTab !== 'chats' && rooms.filter(r => !searchQuery || (r.name || '').toLowerCase().includes(searchQuery.toLowerCase())).map((room) => {
          const displayName = getDisplayName(room);
          const avatarColor = getAvatarColor(displayName);

          if (activeTab === 'departments') {
            // Hide non-member departments from non-admin users
            if (!room.isMember && role !== 'admin') return null;
            return (
              <div key={room.id} className="flex items-center gap-3 px-4 py-3 hover:bg-[#f0f0f0] cursor-pointer">
              <Avatar className="h-12 w-12 shrink-0">
                  {room.icon_url ? (
                    <AvatarImage src={room.icon_url} alt={room.name || ''} />
                  ) : null}
                  <AvatarFallback style={{ backgroundColor: avatarColor }} className="text-white text-lg font-medium">
                    <Building2 className="h-5 w-5" />
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0" onClick={() => room.isMember && onSelectRoom(room)}>
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-[#111b21] text-base truncate">{room.name}</h4>
                    <div className="flex items-center gap-1.5">
                      {isRoomMuted(room.id) && <VolumeX className="h-3.5 w-3.5 text-[#667781]" />}
                      {room.lastMessageTime && <span className="text-[11px] text-[#667781]">{formatTime(room.lastMessageTime)}</span>}
                      {unreadByRoom[room.id] > 0 && (
                        <span className="min-w-[20px] h-5 px-1 rounded-full bg-[#25D366] text-white text-xs font-bold flex items-center justify-center">
                          {unreadByRoom[room.id] > 99 ? '99+' : unreadByRoom[room.id]}
                        </span>
                      )}
                    </div>
                  </div>
                  {room.isMember && room.lastMessage && (
                    <p className="text-sm text-[#667781] truncate">{room.lastMessage}</p>
                  )}
                </div>
                {!room.isMember && role === 'admin' && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-[#25D366] hover:text-[#128C7E]"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleJoinDepartment(room.id);
                    }}
                  >
                    <LogIn className="h-4 w-4" />
                    <span className="text-xs mr-1">{t('joinDepartment')}</span>
                  </Button>
                )}
              </div>
            );
          }

          return (
            <div
              key={room.id}
              className="flex items-center gap-3 px-4 py-3 hover:bg-[#f0f0f0] cursor-pointer border-b border-[#e9e9e9]"
              onClick={() => onSelectRoom(room)}
            >
              <Avatar className="h-12 w-12 shrink-0">
                {room.icon_url ? (
                  <AvatarImage src={room.icon_url} alt={displayName} />
                ) : null}
                <AvatarFallback style={{ backgroundColor: avatarColor }} className="text-white text-lg font-medium">
                  <Users className="h-5 w-5" />
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-[#111b21] text-base truncate">{displayName}</h4>
                  <div className="flex items-center gap-1.5">
                    {room.lastMessageTime && <span className="text-[11px] text-[#667781]">{formatTime(room.lastMessageTime)}</span>}
                    {unreadByRoom[room.id] > 0 && (
                      <span className="min-w-[20px] h-5 px-1 rounded-full bg-[#25D366] text-white text-xs font-bold flex items-center justify-center">
                        {unreadByRoom[room.id] > 99 ? '99+' : unreadByRoom[room.id]}
                      </span>
                    )}
                  </div>
                </div>
                {room.lastMessage && (
                  <p className="text-sm text-[#667781] truncate">{room.lastMessage}</p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* FAB for new group */}
      {activeTab === 'groups' && (
        <button
          onClick={() => setShowCreateGroup(true)}
          className="absolute bottom-4 left-4 rtl:left-auto rtl:right-4 w-14 h-14 rounded-full bg-[#25D366] text-white shadow-lg flex items-center justify-center hover:bg-[#128C7E] transition-all hover:scale-105"
        >
          <Users className="h-6 w-6" />
        </button>
      )}

      <NewChatDialog open={showNewChat} onClose={() => setShowNewChat(false)} onCreated={fetchRooms} />
      <CreateGroupDialog open={showCreateGroup} onClose={() => setShowCreateGroup(false)} onCreated={fetchRooms} />
    </div>
  );
};

export default ChatList;
